import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const SafetyStock = () => {
  // State variables for Safety Stock calculation
  const [maxDailyUsage, setMaxDailyUsage] = useState(0); // Maximum daily usage
  const [maxLeadTime, setMaxLeadTime] = useState(0); // Maximum lead time in days
  const [avgDailyUsage, setAvgDailyUsage] = useState(0); // Average daily usage
  const [avgLeadTime, setAvgLeadTime] = useState(0); // Average lead time in days
  const [safetyStock, setSafetyStock] = useState(0); // Calculated safety stock

  // Event handlers to update state values
  const handleMaxDailyUsageChange = (e) =>
    setMaxDailyUsage(parseFloat(e.target.value) || 0);
  const handleMaxLeadTimeChange = (e) =>
    setMaxLeadTime(parseInt(e.target.value) || 0);
  const handleAvgDailyUsageChange = (e) =>
    setAvgDailyUsage(parseFloat(e.target.value) || 0);
  const handleAvgLeadTimeChange = (e) =>
    setAvgLeadTime(parseInt(e.target.value) || 0);

  // useEffect hook to calculate safety stock when any input changes
  useEffect(() => {
    const safetyStockValue =
      maxDailyUsage * maxLeadTime - avgDailyUsage * avgLeadTime;
    setSafetyStock(safetyStockValue > 0 ? safetyStockValue : 0);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [maxDailyUsage, maxLeadTime, avgDailyUsage, avgLeadTime]);

  return (
    <Layout title="Safety Stock Calculator" path="safety-stock">
      <section className="section-padding" id="safety-stock-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* Safety Stock Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Safety Stock Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Maximum Daily Usage Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="maxDailyUsage" className="form-label">
                        Maximum Daily Usage
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Maximum Daily Usage:</strong> The highest quantity of units consumed or sold per day in peak demand."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="maxDailyUsage"
                        placeholder="Enter maximum daily usage"
                        value={maxDailyUsage || ""}
                        onChange={handleMaxDailyUsageChange}
                      />
                    </div>

                    {/* Maximum Lead Time Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="maxLeadTime" className="form-label">
                        Maximum Lead Time (Days)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Maximum Lead Time:</strong> The longest duration (in days) it takes for a supplier to deliver goods."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="maxLeadTime"
                        placeholder="Enter maximum lead time in days"
                        value={maxLeadTime || ""}
                        onChange={handleMaxLeadTimeChange}
                      />
                    </div>

                    {/* Average Daily Usage Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="avgDailyUsage" className="form-label">
                        Average Daily Usage
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Average Daily Usage:</strong> The typical quantity of units consumed or sold per day over a period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="avgDailyUsage"
                        placeholder="Enter average daily usage"
                        value={avgDailyUsage || ""}
                        onChange={handleAvgDailyUsageChange}
                      />
                    </div>

                    {/* Average Lead Time Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="avgLeadTime" className="form-label">
                        Average Lead Time (Days)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Average Lead Time:</strong> The typical duration (in days) it takes for a supplier to deliver goods."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="avgLeadTime"
                        placeholder="Enter average lead time in days"
                        value={avgLeadTime || ""}
                        onChange={handleAvgLeadTimeChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Safety Stock:{" "}
                        <span className="fw-bold ms-3">
                          {safetyStock === 0
                            ? "0"
                            : safetyStock.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          Units
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Safety Stock Calculator
                </h3>

                {/* What is a Safety Stock Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Safety Stock Calculator?
                </h4>
                <p>
                  The **Safety Stock Calculator** determines the amount of
                  buffer inventory needed to prevent stockouts. It uses daily
                  usage and lead time values to help businesses maintain smooth
                  operations.
                </p>

                {/* Why Use a Safety Stock Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use a Safety Stock Calculator?
                </h4>
                <p>
                  This calculator is essential for inventory and supply chain
                  management. It ensures you always have sufficient stock to
                  meet demand, even during fluctuations or delays.
                </p>

                {/* Benefits of Using a Safety Stock Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Safety Stock Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Prevents Stockouts:</strong> Ensures inventory is
                    available during unexpected demand or supply delays.
                  </li>
                  <li>
                    <strong>Improves Customer Satisfaction:</strong> Reduces the
                    risk of lost sales due to unavailable stock.
                  </li>
                  <li>
                    <strong>Balances Inventory Costs:</strong> Helps avoid
                    excessive inventory holding costs while maintaining
                    readiness.
                  </li>
                  <li>
                    <strong>Supports Supply Chain Optimization:</strong> Helps
                    plan inventory levels based on real usage and lead times.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use a Safety Stock Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Retailers:</strong> Helps manage inventory for
                    retail operations.
                  </li>
                  <li>
                    <strong>Manufacturers:</strong> Ensures raw materials are
                    always available for production.
                  </li>
                  <li>
                    <strong>Supply Chain Managers:</strong> Optimizes inventory
                    levels to balance cost and availability.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How do I determine accurate usage and lead time values?
                </h4>
                <p>
                  To determine accurate values, track daily usage and lead times
                  over a period of time. Use averages for regular operations and
                  maximums for peak demand or delays.
                </p>

                <h4 className="mt-4 mb-3">
                  What happens if I don’t calculate safety stock?
                </h4>
                <p>
                  Without safety stock, businesses risk frequent stockouts, lost
                  sales, and dissatisfied customers. Proper calculation
                  minimizes these risks while managing costs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default SafetyStock;
